import { Link } from 'react-router-dom';
import {
    Sparkles,
    Zap,
    Brain,
    BarChart3,
    Share2,
    Mail,
    Search,
    Megaphone,
    Wallet,
    ArrowRight,
    CheckCircle,
    Play,
    Star,
    Globe,
    Shield,
    Cpu,
    MessageSquare,
    Users,
    Rocket,
    Image,
    Layout,
    Bell,
    Code,
    FileText,
    Settings,
    Briefcase,
    Bot,
    Database,
    ChevronDown
} from 'lucide-react';
import { useState } from 'react';

export default function Home() {
    const features = [
        // === MARKETING MODULES ===
        {
            icon: Search,
            title: 'SEO Automation',
            description: 'AI audits, keywords, backlinks, SERP tracking.',
            color: 'from-blue-500 to-cyan-500'
        },
        {
            icon: Share2,
            title: 'Social Media',
            description: 'Scheduling, carousels, video scripts, listening.',
            color: 'from-pink-500 to-rose-500'
        },
        {
            icon: Megaphone,
            title: 'Ad Campaigns',
            description: 'Google, Meta, TikTok ads & optimization.',
            color: 'from-orange-500 to-amber-500'
        },
        {
            icon: Mail,
            title: 'Email Marketing',
            description: 'Sequences, A/B tests, personalization.',
            color: 'from-green-500 to-emerald-500'
        },
        {
            icon: Layout,
            title: 'Landing Pages',
            description: 'AI page builder, forms, popups.',
            color: 'from-teal-500 to-cyan-500'
        },
        {
            icon: BarChart3,
            title: 'Analytics',
            description: 'Funnels, cohorts, attribution, predictions.',
            color: 'from-indigo-500 to-blue-500'
        },
        {
            icon: Users,
            title: 'CRM & Pipeline',
            description: 'Lead scoring, deals, activity tracking.',
            color: 'from-violet-500 to-purple-500'
        },
        {
            icon: MessageSquare,
            title: 'Chatbot & Support',
            description: 'AI chatbot, tickets, live chat.',
            color: 'from-sky-500 to-blue-500'
        },
        {
            icon: Rocket,
            title: 'Growth Hacking',
            description: 'Referrals, viral loops, Product Hunt.',
            color: 'from-red-500 to-orange-500'
        },
        // === AI ECOSYSTEM MODULES ===
        {
            icon: Code,
            title: 'Developer AI',
            description: 'Code gen, review, tests, debugging, APIs.',
            color: 'from-cyan-500 to-blue-500'
        },
        {
            icon: FileText,
            title: 'Document AI',
            description: 'Summarize, extract, translate, OCR.',
            color: 'from-slate-500 to-gray-500'
        },
        {
            icon: Database,
            title: 'ML/AI Ops',
            description: 'Embeddings, vectors, classification, NER.',
            color: 'from-purple-500 to-violet-500'
        },
        {
            icon: Briefcase,
            title: 'Business Ops',
            description: 'HR, finance, legal, proposals, OKRs.',
            color: 'from-amber-500 to-orange-500'
        },
        {
            icon: Bot,
            title: 'Agent Framework',
            description: 'Custom agents, chaining, orchestration.',
            color: 'from-rose-500 to-pink-500'
        },
        {
            icon: Globe,
            title: 'Data & Research',
            description: 'Web search, scraping, analysis, charts.',
            color: 'from-lime-500 to-green-500'
        },
        {
            icon: Bell,
            title: 'Notifications',
            description: 'Push, SMS, WhatsApp, webhooks.',
            color: 'from-yellow-500 to-amber-500'
        },
        {
            icon: Image,
            title: 'Creative Studio',
            description: 'AI images, video, audio, presentations.',
            color: 'from-fuchsia-500 to-pink-500'
        },
        {
            icon: Wallet,
            title: 'Payments & Billing',
            description: 'Subscriptions, invoices, churn prediction.',
            color: 'from-emerald-500 to-green-500'
        }
    ];

    const aiCapabilities = [
        {
            icon: Brain,
            title: 'RAG (Retrieval-Augmented Generation)',
            description: 'Contextual AI responses using your historical data and documents.'
        },
        {
            icon: Zap,
            title: 'TAG (Tool-Augmented Generation)',
            description: 'Real-time actions with 220+ integrated tools for live data and automation.'
        },
        {
            icon: Cpu,
            title: 'MCP (Model Context Protocol)',
            description: 'Connect AI agents directly to your aiOps workspace via standardized protocol.'
        }
    ];

    const stats = [
        { value: '220+', label: 'AI Tools' },
        { value: '18', label: 'Modules' },
        { value: '99.9%', label: 'Uptime' },
        { value: '< 50ms', label: 'Edge Latency' }
    ];

    const [productsOpen, setProductsOpen] = useState(false);

    return (
        <div className="min-h-screen bg-dark-950">
            {/* Navigation */}
            <nav className="fixed top-0 left-0 right-0 z-50 bg-dark-950/80 backdrop-blur-lg border-b border-dark-800">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between h-16">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-gradient-to-br from-primary-500 to-primary-700 rounded-xl flex items-center justify-center">
                                <Sparkles className="w-6 h-6 text-white" />
                            </div>
                            <span className="text-xl font-bold">aiOps</span>
                        </div>
                        <div className="hidden md:flex items-center gap-8">
                            {/* Products Dropdown */}
                            <div className="relative">
                                <button
                                    onClick={() => setProductsOpen(!productsOpen)}
                                    onBlur={() => setTimeout(() => setProductsOpen(false), 150)}
                                    className="flex items-center gap-1 text-dark-300 hover:text-white transition"
                                >
                                    Products
                                    <ChevronDown className={`w-4 h-4 transition-transform ${productsOpen ? 'rotate-180' : ''}`} />
                                </button>
                                {productsOpen && (
                                    <div className="absolute top-full left-0 mt-2 w-64 bg-dark-900 border border-dark-700 rounded-xl shadow-xl overflow-hidden">
                                        <Link
                                            to="/products/marketing-automation"
                                            className="flex items-center gap-3 px-4 py-3 hover:bg-dark-800 transition"
                                        >
                                            <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-red-500 rounded-lg flex items-center justify-center">
                                                <Zap className="w-5 h-5 text-white" />
                                            </div>
                                            <div>
                                                <div className="font-semibold text-white">Automation</div>
                                                <div className="text-xs text-dark-400">Digital Marketing Automation</div>
                                            </div>
                                        </Link>
                                        <Link
                                            to="/products/devai"
                                            className="flex items-center gap-3 px-4 py-3 hover:bg-dark-800 transition border-t border-dark-700"
                                        >
                                            <div className="w-10 h-10 bg-gradient-to-br from-cyan-500 to-blue-500 rounded-lg flex items-center justify-center">
                                                <Code className="w-5 h-5 text-white" />
                                            </div>
                                            <div>
                                                <div className="font-semibold text-white">DevAI</div>
                                                <div className="text-xs text-dark-400">Developer AI Tools</div>
                                            </div>
                                        </Link>
                                        <Link
                                            to="/products/bizops"
                                            className="flex items-center gap-3 px-4 py-3 hover:bg-dark-800 transition border-t border-dark-700"
                                        >
                                            <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-violet-500 rounded-lg flex items-center justify-center">
                                                <Briefcase className="w-5 h-5 text-white" />
                                            </div>
                                            <div>
                                                <div className="font-semibold text-white">BizOps</div>
                                                <div className="text-xs text-dark-400">Business Operations AI</div>
                                            </div>
                                        </Link>
                                    </div>
                                )}
                            </div>
                            <a href="#features" className="text-dark-300 hover:text-white transition">Features</a>
                            <a href="#ai" className="text-dark-300 hover:text-white transition">AI Architecture</a>
                            <a href="#modules" className="text-dark-300 hover:text-white transition">Modules</a>
                            <a href="/features.html" className="text-dark-300 hover:text-white transition">Docs</a>
                        </div>
                        <div className="flex items-center gap-4">
                            <Link to="/login" className="text-dark-300 hover:text-white transition">
                                Sign in
                            </Link>
                            <Link to="/register" className="btn btn-primary">
                                Start Free Trial
                            </Link>
                        </div>
                    </div>
                </div>
            </nav>

            {/* Hero Section */}
            <section className="pt-32 pb-20 px-4 relative overflow-hidden">
                {/* Background gradient */}
                <div className="absolute inset-0 bg-gradient-to-b from-primary-600/10 via-transparent to-transparent" />
                <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary-600/20 rounded-full blur-3xl" />
                <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-purple-600/20 rounded-full blur-3xl" />

                <div className="max-w-7xl mx-auto text-center relative z-10">
                    <div className="inline-flex items-center gap-2 bg-dark-800 border border-dark-700 rounded-full px-4 py-2 mb-8">
                        <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
                        <span className="text-sm text-dark-300">Powered by RAG + TAG + MCP Architecture</span>
                    </div>

                    <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight">
                        Your Complete<br />
                        <span className="bg-gradient-to-r from-primary-400 via-purple-400 to-cyan-400 bg-clip-text text-transparent">
                            AI Ecosystem
                        </span>
                    </h1>

                    <p className="text-xl text-dark-300 max-w-3xl mx-auto mb-10">
                        Access powerful AI capabilities for marketing, automation, content generation,
                        analytics, and beyond — all in one unified platform built on Cloudflare's edge.
                    </p>

                    <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-16">
                        <Link to="/register" className="btn btn-primary text-lg px-8 py-4 flex items-center gap-2">
                            Get Started Free
                            <ArrowRight className="w-5 h-5" />
                        </Link>
                        <a href="#demo" className="btn btn-secondary text-lg px-8 py-4 flex items-center gap-2">
                            <Play className="w-5 h-5" />
                            Watch Demo
                        </a>
                    </div>

                    {/* Stats */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-3xl mx-auto">
                        {stats.map((stat) => (
                            <div key={stat.label} className="text-center">
                                <div className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-primary-400 to-purple-400 bg-clip-text text-transparent">
                                    {stat.value}
                                </div>
                                <div className="text-dark-400 text-sm mt-1">{stat.label}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* AI Architecture Section */}
            <section id="ai" className="py-20 px-4 bg-dark-900/50">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl md:text-5xl font-bold mb-4">
                            Advanced <span className="text-primary-400">AI Architecture</span>
                        </h2>
                        <p className="text-dark-300 text-lg max-w-2xl mx-auto">
                            Three powerful AI paradigms working together to deliver intelligent, contextual, and actionable results.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-3 gap-8">
                        {aiCapabilities.map((cap) => (
                            <div key={cap.title} className="card p-8 hover:border-primary-500/50 transition-all">
                                <div className="w-14 h-14 bg-gradient-to-br from-primary-500 to-purple-600 rounded-2xl flex items-center justify-center mb-6">
                                    <cap.icon className="w-7 h-7 text-white" />
                                </div>
                                <h3 className="text-xl font-semibold mb-3">{cap.title}</h3>
                                <p className="text-dark-400">{cap.description}</p>
                            </div>
                        ))}
                    </div>

                    {/* MCP Tools Preview */}
                    <div className="mt-16 card p-8">
                        <div className="flex items-center gap-3 mb-6">
                            <Cpu className="w-6 h-6 text-primary-400" />
                            <h3 className="text-xl font-semibold">220+ MCP Tools Available</h3>
                        </div>
                        <div className="flex flex-wrap gap-2">
                            {[
                                // SEO Tools
                                'run_seo_audit', 'research_keywords', 'generate_seo_content', 'get_keyword_rankings',
                                'analyze_competitors', 'backlink_analysis', 'technical_seo_scan', 'local_seo_optimize',
                                'content_gap_analysis', 'serp_tracking', 'internal_linking_suggest',
                                // Content Tools
                                'generate_blog_post', 'generate_product_description', 'generate_landing_page',
                                'rewrite_content', 'check_plagiarism', 'optimize_headlines', 'generate_faq',
                                // Social Tools
                                'generate_social_post', 'schedule_social_post', 'social_calendar_create',
                                'hashtag_research', 'social_listening', 'influencer_find', 'generate_carousel',
                                'generate_thread', 'generate_video_script', 'repurpose_content',
                                // Ad Tools
                                'create_ad_campaign', 'optimize_ad_campaign', 'ad_copy_generate',
                                'audience_builder', 'budget_optimizer', 'retargeting_setup',
                                // Email Tools
                                'generate_email', 'email_sequence_create', 'subject_line_generate',
                                'email_deliverability_check', 'ab_test_email', 'list_segmentation',
                                // Analytics Tools
                                'funnel_analysis', 'cohort_analysis', 'attribution_model', 'predictive_analytics',
                                'anomaly_detection', 'custom_report_build', 'chat_with_data',
                                // AI & Automation
                                'rag_search', 'rag_generate', 'tag_execute_tool', 'workflow_create',
                                'marketing_strategy_generate', 'competitor_monitor', 'trend_spotter'
                            ].map((tool) => (
                                <span key={tool} className="px-3 py-1 bg-dark-800 border border-dark-700 rounded-full text-sm text-dark-300">
                                    {tool}
                                </span>
                            ))}
                            <span className="px-3 py-1 bg-primary-600/20 border border-primary-500/30 rounded-full text-sm text-primary-400">
                                +150 more tools...
                            </span>
                        </div>
                    </div>
                </div>
            </section>

            {/* Features Grid */}
            <section id="features" className="py-20 px-4">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl md:text-5xl font-bold mb-4">
                            <span className="text-primary-400">18 Modules</span> — Complete AI Ecosystem
                        </h2>
                        <p className="text-dark-300 text-lg max-w-2xl mx-auto">
                            Marketing automation + Developer AI + Business ops — all unified in one platform.
                        </p>
                    </div>

                    <div id="modules" className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                        {features.map((feature) => (
                            <div key={feature.title} className="card p-5 hover:border-primary-500/50 transition-all group">
                                <div className={`w-10 h-10 bg-gradient-to-br ${feature.color} rounded-lg flex items-center justify-center mb-3 group-hover:scale-110 transition-transform`}>
                                    <feature.icon className="w-5 h-5 text-white" />
                                </div>
                                <h3 className="text-base font-semibold mb-1">{feature.title}</h3>
                                <p className="text-dark-400 text-xs">{feature.description}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Why aiOps */}
            <section className="py-20 px-4 bg-dark-900/50">
                <div className="max-w-7xl mx-auto">
                    <div className="grid lg:grid-cols-2 gap-12 items-center">
                        <div>
                            <h2 className="text-3xl md:text-5xl font-bold mb-6">
                                Why Choose <span className="text-primary-400">aiOps</span>?
                            </h2>
                            <div className="space-y-6">
                                {[
                                    {
                                        icon: Globe,
                                        title: 'Edge-First Architecture',
                                        description: 'Built on Cloudflare Workers for sub-50ms latency worldwide.'
                                    },
                                    {
                                        icon: Shield,
                                        title: 'Enterprise Security',
                                        description: 'PBKDF2 password hashing, refresh tokens, GDPR compliance built-in.'
                                    },
                                    {
                                        icon: Cpu,
                                        title: 'AI Agent Ready',
                                        description: 'MCP server allows any AI agent to control your marketing stack.'
                                    },
                                    {
                                        icon: Star,
                                        title: 'Multi-Tenant SaaS',
                                        description: 'Organizations, teams, API keys, and subscription plans out of the box.'
                                    }
                                ].map((item) => (
                                    <div key={item.title} className="flex gap-4">
                                        <div className="w-10 h-10 bg-primary-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                                            <item.icon className="w-5 h-5 text-primary-400" />
                                        </div>
                                        <div>
                                            <h3 className="font-semibold mb-1">{item.title}</h3>
                                            <p className="text-dark-400 text-sm">{item.description}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div className="card p-8">
                            <div className="text-sm text-dark-400 mb-4">Quick Start</div>
                            <div className="bg-dark-950 rounded-lg p-4 font-mono text-sm overflow-x-auto">
                                <div className="text-dark-500"># Connect AI agent via MCP</div>
                                <div className="text-green-400">curl -X POST \</div>
                                <div className="text-dark-300 pl-4">https://api.ai-operations.pages.dev/mcp \</div>
                                <div className="text-dark-300 pl-4">-H "X-API-Key: mcp_xxxxx" \</div>
                                <div className="text-dark-300 pl-4">-d '{`{"tool": "seo_audit", "args": {...}}`}'</div>
                            </div>
                            <div className="mt-6 flex items-center gap-2 text-dark-400 text-sm">
                                <CheckCircle className="w-4 h-4 text-green-400" />
                                <span>28 tools available via MCP</span>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-20 px-4">
                <div className="max-w-4xl mx-auto">
                    <div className="card p-12 text-center bg-gradient-to-br from-primary-600/20 via-dark-900 to-purple-600/20 border-primary-500/30">
                        <h2 className="text-3xl md:text-5xl font-bold mb-4">
                            Ready to Access Your AI Ecosystem?
                        </h2>
                        <p className="text-dark-300 text-lg mb-8 max-w-2xl mx-auto">
                            Start your 14-day free trial. No credit card required.
                            Get full access to all modules and AI capabilities.
                        </p>
                        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                            <Link to="/register" className="btn btn-primary text-lg px-8 py-4 flex items-center gap-2">
                                Start Free Trial
                                <ArrowRight className="w-5 h-5" />
                            </Link>
                            <a href="https://github.com/dadyai/aiops" className="btn btn-secondary text-lg px-8 py-4 flex items-center gap-2">
                                <Star className="w-5 h-5" />
                                Star on GitHub
                            </a>
                        </div>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="py-12 px-4 border-t border-dark-800">
                <div className="max-w-7xl mx-auto">
                    <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                        <div className="flex items-center gap-3">
                            <div className="w-8 h-8 bg-gradient-to-br from-primary-500 to-primary-700 rounded-lg flex items-center justify-center">
                                <Sparkles className="w-4 h-4 text-white" />
                            </div>
                            <span className="font-bold">aiOps</span>
                            <span className="text-dark-500">|</span>
                            <span className="text-dark-400 text-sm">AI Operations Platform</span>
                        </div>
                        <div className="flex items-center gap-6 text-sm text-dark-400">
                            <a href="/features.html" className="hover:text-white transition">Features</a>
                            <a href="https://github.com/dadyai/aiops" className="hover:text-white transition">GitHub</a>
                            <a href="#" className="hover:text-white transition">Privacy</a>
                            <a href="#" className="hover:text-white transition">Terms</a>
                        </div>
                    </div>
                    <div className="mt-8 text-center text-dark-500 text-sm">
                        © 2026 aiOps (AI Operations). Built with ❤️ on Cloudflare.
                    </div>
                </div>
            </footer>
        </div>
    );
}
